import * as z from "zod";
import { TFunction } from "i18next";

// Helper for required strings
const requiredString = (t: TFunction, key: string, message?: string) =>
  z.string().min(1, message || t(key));

// Helper for strict password rules
const strictPassword = (t: TFunction) =>
  z
    .string()
    .min(8, t("user.validation.weakPassword"))
    .regex(/[A-Z]/, t("user.validation.weakPassword"))
    .regex(/[0-9]/, t("user.validation.weakPassword"));

// Schema for the Login Form
export const createLoginSchema = (t: TFunction) =>
  z.object({
    username: requiredString(t, "user.validation.usernameRequired"),
    password: requiredString(t, "user.validation.passwordRequired"),
    remember: z.boolean().optional(),
    nickname: z.string().optional(), // Honeypot field
    form_load_timestamp: z.number().optional(), // Timestamp field
  });

export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

// Schema for the Register Form
export const createRegisterSchema = (t: TFunction) =>
  z.object({
    email: requiredString(t, "validation.emailInvalid").email(
      t("validation.emailInvalid"),
    ),
    username: z.string().optional(),
    password: strictPassword(t),
    // --- MAILING CONSENT (NEW) ---
    marketing_optin: z.boolean().optional(),
    agreement: z.literal(true, {
      // Changed 'errorMap' to 'message' to satisfy Typescript and Zod's API
      message: t(
        "user.validation.agreementRequired",
        "You must agree to the membership terms.",
      ),
    }),
    nickname: z.string().optional(), // Honeypot field
    form_load_timestamp: z.number().optional(), // Timestamp field
  });

export type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

// Schema for the Account Details Form
export const createAccountDetailsSchema = (t: TFunction) =>
  z
    .object({
      // Allow empty strings for names
      first_name: z.string(),
      last_name: z.string(),
      display_name: requiredString(t, "user.validation.displayNameRequired"),
      email: requiredString(t, "validation.emailInvalid").email(
        t("validation.emailInvalid"),
      ),
      password_current: z.string().optional(),
      password_new: z.string().optional(),
      password_confirm: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      // 1. If user enters a new password, they MUST provide the current password.
      if (data.password_new && !data.password_current) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("user.validation.currentPasswordRequired"),
          path: ["password_current"],
        });
      }

      // 2. If user enters current password, they MUST provide a new password.
      if (data.password_current && !data.password_new) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            "user.validation.newPasswordRequired",
            "Please enter a new password.",
          ),
          path: ["password_new"],
        });
      }

      // 3. New password must be strong (manual check since it's optional in schema)
      if (data.password_new) {
        const password = data.password_new;
        if (
          password.length < 8 ||
          !/[A-Z]/.test(password) ||
          !/[0-9]/.test(password)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("user.validation.weakPassword"),
            path: ["password_new"],
          });
        }
      }

      // 4. New password and confirm password must match.
      if (data.password_new !== data.password_confirm) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("user.validation.passwordsDoNotMatch"),
          path: ["password_confirm"],
        });
      }
    });

export type AccountDetailsFormData = z.infer<
  ReturnType<typeof createAccountDetailsSchema>
>;

// --- Password Reset Schemas ---

export const createForgotPasswordSchema = (t: TFunction) =>
  z.object({
    email: z.string().email(t("validation.emailInvalid")),
    // Security fields
    nickname: z.string().optional(),
    form_load_timestamp: z.number().optional(),
  });

export type ForgotPasswordFormData = z.infer<
  ReturnType<typeof createForgotPasswordSchema>
>;

export const createResetPasswordSchema = (t: TFunction) =>
  z
    .object({
      password: strictPassword(t),
      password_confirm: z.string(),
    })
    .refine((data) => data.password === data.password_confirm, {
      message: t("user.validation.passwordsDoNotMatch"),
      path: ["password_confirm"],
    });

export type ResetPasswordFormData = z.infer<
  ReturnType<typeof createResetPasswordSchema>
>;
